import React, { useEffect, useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Box,
  Typography,
} from '@mui/material';
import { Users } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authstore';
import { trackEvent } from '../../lib/mixpanel';
import { ROUTES } from '../../router/routes';
import { decodeJwtToken } from '../../utils';

const UserMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { session } = useAuthStore();
  const reopenProfilePicker = useAuthStore((s) => (s as unknown as { reopenProfilePicker: () => Promise<void> }).reopenProfilePicker);
  const navigate = useNavigate();
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>(undefined);

  const displayName =
    [session?.user?.firstName, session?.user?.lastName].filter(Boolean).join(' ') ||
    session?.user?.username ||
    '';

  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') ||
    (session?.user?.username?.charAt(0).toUpperCase() ?? 'U');

  const isParentSession = (() => {
    if (!session?.accessToken) return false;
    try {
      const decoded = decodeJwtToken(session.accessToken) as Record<string, unknown>;
      return !!decoded?.psel_parent;
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (session?.user.profileImageUrl instanceof File) {
      setProfileImageUrl(URL.createObjectURL(session.user.profileImageUrl));
    } else {
      setProfileImageUrl(session?.user.profileImageUrl as unknown as string | undefined);
    }
  }, [session]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Profile lives in the sidebar — header menu only for parent "Switch child"
    if (!isParentSession) return;
    setAnchorEl(event.currentTarget);
    trackEvent('Profile Menu Opened');
  };

  const handleSwitchChild = async () => {
    handleClose();
    trackEvent('Switch Child Clicked');
    try {
      await reopenProfilePicker();
      navigate(ROUTES.AUTH.SELECT_PROFILE);
    } catch {
      navigate(ROUTES.AUTH.LOGIN);
    }
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          cursor: isParentSession ? 'pointer' : 'default',
          px: 1,
          py: 0.5,
          borderRadius: '10px',
          bgcolor: 'rgba(138, 112, 214, 0.1)',
          border: '1.5px solid rgba(138, 112, 214, 0.25)',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'rgba(138, 112, 214, 0.18)',
            borderColor: 'rgba(138, 112, 214, 0.45)',
            boxShadow: '0 0 0 3px rgba(138, 112, 214, 0.1)',
          },
        }}
      >
        {profileImageUrl ? (
          <Avatar
            sx={{ width: 32, height: 32, borderRadius: '8px' }}
            src={profileImageUrl}
          />
        ) : (
          <Avatar
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: 'primary.main',
              fontSize: '0.85rem',
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        )}
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {displayName}
        </Typography>
      </Box>
      {isParentSession && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleSwitchChild}>
            <ListItemIcon>
              <Users size={18} />
            </ListItemIcon>
            <ListItemText>Switch child</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default UserMenu;
